import React, { ReactNode } from "react";
import { UIUtils } from "../utils/uiUtils";

interface ButtonProps {
  /** The text content of the button */
  text?: string;
  /** Function to be called when button is clicked */
  onClick?: () => void;
  /** Button variant - secondary is default (black button) */
  variant?: "primary" | "secondary" | "tertiary" | "light" | "nav" | "unstyled";
  /** Text to show when button is in loading state */
  loadingText?: string;
  /** Whether the button is in loading state */
  isLoading?: boolean;
  /** Type of the button */
  type?: "button" | "submit" | "reset";
  /** Additional CSS classes */
  className?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Title attribute for tooltip and accessibility */
  title?: string;
  /** Optional icon to display before the text */
  icon?: string;
  /** Alt text for the icon */
  iconAlt?: string;
  /** Optional custom content instead of text/icon layout */
  children?: ReactNode;
  /** Optional class for the default content wrapper */
  contentClassName?: string;
  /** Optional aria-label attribute */
  ariaLabel?: string;
  /** Optional aria-pressed attribute for toggle buttons */
  ariaPressed?: boolean;
  /** Disable click tracking if needed */
  trackClick?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  text = "",
  children,
  onClick,
  variant = "secondary",
  loadingText = "Loading...",
  isLoading = false,
  type = "button",
  className = "",
  disabled = false,
  title,
  icon,
  iconAlt,
  contentClassName = "flex items-center justify-center gap-2",
  ariaLabel,
  ariaPressed,
  trackClick = true,
}) => {
  const handleClick = () => {
    if (trackClick) {
      UIUtils.trackButtonClick(text || ariaLabel || "button");
    }
    onClick?.();
  };

  // Define variant styles based on static site CSS specifications
  const variantStyles = {
    primary: {
      base: "bg-primary text-black",
      hover: "hover:bg-primary-hover",
      focus: "focus:ring-primary",
      disabled: "disabled:bg-primary disabled:opacity-35 disabled:text-black",
    },
    secondary: {
      base: "bg-black text-white",
      hover: "hover:bg-dark-grey",
      focus: "focus:ring-black",
      disabled: "disabled:bg-black disabled:opacity-10 disabled:text-white",
    },
    tertiary: {
      base: "bg-transparent text-black border-[1.5px] border-grey-60",
      hover: "hover:border-grey-80",
      focus: "focus:ring-grey-80",
      disabled: "disabled:opacity-25 disabled:border-grey-60",
    },
    light: {
      base: "bg-grey-20 text-black",
      hover: "hover:border-grey-60",
      focus: "focus:ring-black",
      disabled: "disabled:bg-white disabled:opacity-50 disabled:text-black",
    },
    nav: {
      base: "bg-white text-black border-[1.5px] border-grey-20",
      hover: "hover:border-grey-60",
      focus: "focus:ring-black",
      disabled: "disabled:bg-white disabled:opacity-50 disabled:text-black",
    },
    unstyled: {
      base: "",
      hover: "",
      focus: "",
      disabled: "disabled:opacity-50 disabled:cursor-not-allowed",
    },
  };

  const currentVariant = variantStyles[variant];
  const isUnstyled = variant === "unstyled";
  const defaultSizingClasses = isUnstyled
    ? ""
    : `
        px-4 
        py-3 
        text-sm
        md:px-[18px] 
        md:py-3 
        md:text-[17px]
        rounded-30
        font-semibold
        disabled:cursor-not-allowed
        transition-all
        duration-200
        focus:outline-none
        focus:ring
        focus:ring-offset-2
        min-w-[100px]
        text-center
      `;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center">
          <svg
            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>{loadingText}</span>
        </div>
      );
    }

    if (children) {
      return children;
    }

    return (
      <div className={contentClassName}>
        {icon && (
          <img src={icon} alt={iconAlt || ""} className="w-5 h-5 mr-1" />
        )}
        <span>{text}</span>
      </div>
    );
  };

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={isLoading || disabled}
      title={title}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      className={`
        ${currentVariant.base}
        ${currentVariant.hover}
        ${currentVariant.focus}
        ${currentVariant.disabled}
        ${defaultSizingClasses}
        ${className}
      `}
    >
      {renderContent()}
    </button>
  );
};
